/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useProductDependencies } from '@atlassian/company-hub-common';

import { LINKS_MENU_EXTENSION_ANALYTIC_SOURCE } from '../../../../../constants';
import { ITEM, type MenuItemType, type Styles } from '../../../../../types';

type MenuItemProps = {
	index?: number;
	isFirstItem?: boolean;
	setWidth?: (width: number, index?: number) => void;
} & Partial<MenuItemType> &
	Partial<Styles>;

const triggerStyles = cssMap({
	firstItem: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		display: 'flex',
		gap: token('space.100'),
		minHeight: '32px',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	firstItemSelected: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderRadius: token('radius.small'),
		display: 'flex',
		gap: token('space.100'),
		minHeight: '32px',
	},
	dropdownItem: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

const selectedDropdownItemStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles
	'button[aria-expanded="true"]': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		color: 'unset',
		'&::before': {
			display: 'none',
		},
	},
});

const displayTextStyles = css({
	display: 'block',
	maxWidth: '200px',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const firstItemStyles = css({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});

type NestedLinkItemProps = {
	childItemId: string;
	childTitle: string;
	link: string;
};

const NestedLinkItem = ({ childItemId, childTitle, link }: NestedLinkItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { useAnalyticsActions } = useProductDependencies();

	const analytics = useAnalyticsActions?.useLinkAnalyticActions?.(link);

	useEffect(() => {
		analytics?.handleView?.();
	}, [analytics, analytics?.handleView]);

	const handleLinkItemClick = () => {
		analytics?.handleClick?.();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link item',
				source: LINKS_MENU_EXTENSION_ANALYTIC_SOURCE,
			},
		}).fire();
	};

	return (
		<DropdownItem key={childItemId} href={link} onClick={handleLinkItemClick} target="_blank">
			{childTitle}
		</DropdownItem>
	);
};

export const MenuItem = ({
	index,
	itemId,
	title,
	childItems,
	isFirstItem,
	highlightColor,
	textColor,
	setWidth,
}: MenuItemProps): JSX.Element => {
	const ref = useRef<HTMLAnchorElement>(null);

	useLayoutEffect(() => {
		if (setWidth && ref.current) {
			setWidth(ref.current.offsetWidth, index);
		}
		// if item title updates, trigger a width recalculation
	}, [setWidth, index, title]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireUIAnalytic = () =>
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'menu item',
				source: LINKS_MENU_EXTENSION_ANALYTIC_SOURCE,
			},
		}).fire();

	return (
		// TODO: Add `shouldRenderToParent` to resolve accessibility issues. See https://a11y.atlassian.net/browse/JCATS-788
		<DropdownMenu
			placement={isFirstItem ? 'bottom-start' : 'right-start'}
			shouldFlip={!isFirstItem}
			trigger={({ triggerRef, ...triggerProps }) =>
				isFirstItem ? (
					<Pressable
						{...triggerProps}
						key={itemId}
						ref={mergeRefs([triggerRef, ref])}
						xcss={
							triggerProps['aria-expanded']
								? triggerStyles.firstItemSelected
								: triggerStyles.firstItem
						}
						onClick={(e) => {
							triggerProps.onClick?.(e);
							fireUIAnalytic();
						}}
					>
						<span
							/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
							css={[firstItemStyles, displayTextStyles]}
							style={{
								color: triggerProps['aria-expanded'] ? highlightColor : textColor,
							}}
							/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
						>
							{title}
						</span>
						<div style={{ color: triggerProps['aria-expanded'] ? highlightColor : textColor }}>
							<ChevronDownIcon label="dropdown-expand" size="small" />
						</div>
					</Pressable>
				) : (
					<div css={selectedDropdownItemStyles}>
						<DropdownItem
							{...triggerProps}
							elemAfter={<ChevronRightIcon label="dropdown-expand" size="small" />}
							key={itemId}
							ref={triggerRef}
							onClick={(e) => {
								triggerProps.onClick?.(e);
								fireUIAnalytic();
							}}
						>
							<span css={displayTextStyles}>{title}</span>
						</DropdownItem>
					</div>
				)
			}
		>
			<DropdownItemGroup>
				{childItems?.map((item) => {
					const { itemId: childItemId, title: childTitle, type } = item;
					if (type === ITEM.MENU) {
						return <MenuItem key={childItemId} title={childTitle} childItems={item.childItems} />;
					}

					const { link } = item;

					return (
						<NestedLinkItem
							key={childItemId}
							childItemId={childItemId}
							childTitle={childTitle}
							link={link}
						/>
					);
				})}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
